import { finalize, takeUntil } from "rxjs/operators";
import { Component, OnInit, ViewChild } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { StatementService } from "../statement.service";
import { Statement } from "../statement";
import { SubscribingComponent } from "app/common";
import { FirebaseEventsService } from "app/common/firebase-events.service";
import { WorkflowControllerService } from "app/core/workflow/workflow-controller/workflow-controller.service";
import { ToolsBarButtonInterface } from "app/shared/components/tools-bar/tools-bar.component";
import { DateHelper } from "app/core/helpers/date.helper";
import { YousignComponent, YousignSignatureStep } from "app/shared/components/yousign-iframe/yousign.component";

@Component({
  selector: "app-vollmacht-authorization",
  templateUrl: "./vollmacht-authorization.component.html",
  styleUrls: ["./vollmacht-authorization.component.scss"],
  standalone: false
})
export class VollmachtAuthorizationComponent extends SubscribingComponent implements OnInit {
  @ViewChild(YousignComponent) private yousignComponent: YousignComponent;
  public statementId: number;
  public statement: Statement;
  public loadingData = false;
  public contentLoaded = false;
  public showAlert = false;
  public acceptThePowerOfAttorney = false;
  public canProceed: boolean = false;
  public signatureStep: YousignSignatureStep = "single";
  public buttons: ToolsBarButtonInterface[] = [
    {
      type: "proceed",
      label: "COMMON.PROCEED",
      action: this.acceptVollmacht.bind(this),
      disabled: () => this.disableProceed()
    }
  ];

  constructor(
    private statementService: StatementService,
    private router: Router,
    private route: ActivatedRoute,
    private firebaseEventsService: FirebaseEventsService,
    public workFlowController: WorkflowControllerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
      this.statementId = parseInt(params["statementId"], 10);
      this.getData();
    });
  }

  public disableProceed(): boolean {
    return !this.canProceed;
  }

  public onSignatureStepChange(step: YousignSignatureStep): void {
    this.signatureStep = step;
  }

  public get titleTranslationKey(): string {
    if (this.signatureStep === "joint-spouse") {
      return this.statement?.isPercentagePaymentAllowed
        ? "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_PLUS_JOINT_SPOUSE"
        : "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_JOINT_SPOUSE";
    }

    if (this.signatureStep === "joint-applicant") {
      return this.statement?.isPercentagePaymentAllowed
        ? "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_PLUS_JOINT_APPLICANT"
        : "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_JOINT_APPLICANT";
    }

    return this.statement?.isPercentagePaymentAllowed
      ? "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_PLUS_SINGLE"
      : "VOLLMACHTAUTHORIZATION.TITLE_PREMIUM_SINGLE";
  }

  public get descriptionTranslationKey(): string {
    if (this.signatureStep === "joint-spouse") {
      return this.statement?.isPercentagePaymentAllowed
        ? "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_PLUS_JOINT_SPOUSE"
        : "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_JOINT_SPOUSE";
    }

    if (this.signatureStep === "joint-applicant") {
      return this.statement?.isPercentagePaymentAllowed
        ? "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_PLUS_JOINT_APPLICANT"
        : "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_JOINT_APPLICANT";
    }

    return this.statement?.isPercentagePaymentAllowed
      ? "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_PLUS_SINGLE"
      : "VOLLMACHTAUTHORIZATION.DESCRIPTION_PREMIUM_SINGLE";
  }

  private acceptVollmacht(): void {
    if (this.signatureStep === "joint-applicant") {
      this.yousignComponent?.goToSpouseSignatureStep();
      this.canProceed = false;
      return;
    }

    this.firebaseEventsService.logEvent("click_accept_proxy", null, this.statementId);

    const data = {
      vollmacht_signed: true,
      vollmacht_authorization_datetime: DateHelper.getFormatted("", "YYYY-MM-DD HH:mm:ss"),
      is_informed_that_cannot_resign_from_claim: true
    };

    this.contentLoaded = false;

    const spouse: Statement = this.statement.spouse;
    if (spouse) {
      Object.assign(data, { spouses: [{ id: spouse.id }] });
    }

    const redirect = () => {
      this.workFlowController.goToTheNextStep();
    };

    const error = (errorStatement: Error): void => {
      this.contentLoaded = true;
      console.log(errorStatement);
    };

    const response = (responseStatement: Statement) => {
      Object.assign(this.statement, responseStatement);

      const handle = () =>
        this.statementService
          .checkOnfidoVollmacht(this.statementId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(redirect, error);

      this.statementService.notifyUser(this.statement.id).pipe(takeUntil(this.ngUnsubscribe)).subscribe(handle, error);
    };

    this.statementService
      .updateStatement(this.statement.id, data)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(response, error);
  }

  private getData(): void {
    this.loadingData = true;
    this.statementService
      .getStatement(this.statementId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => (this.loadingData = false))
      )
      .subscribe(
        (response: Statement) => {
          this.contentLoaded = true;
          this.statement = response;
          this.signatureStep = this.isJointSettlement() ? "joint-applicant" : "single";
          this.workFlowController.init(this.statement);
        },
        () => {
          this.contentLoaded = true;
          this.router.navigate(["/new"]);
        }
      );
  }

  private isJointSettlement(): boolean {
    return !!this.statement?.spouse && !!this.statement?.filedTogether;
  }
}
